var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx } from "react/jsx-runtime";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle } from "@fortawesome/pro-regular-svg-icons";
import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons";
import { formatDisplayDate } from "syf-js-utilities/formats";
import Step from "../../../ui/atoms/Step";
import { StyledToolTip, TooltipWrapper } from "./subcomponents";
import { ExclamationWrapper } from "../../../ui/molecules/DataSections/subcomponents";
import calculateStepperProgress from "./calculateStepperProgress";
import isPredeliveryAndTotalPaymentDueZero from "../../../helpers/isPredeliveryAndTotalPaymentDueZero";
import isLoanClosed from "../../../helpers/isLoanClosed";
import getSubAdditionalInfoForPeriods from "./getSubAdditionalInfoForPeriods";
var getIntroRateLoanDataSections = function (accountSummaryData, autopayStatusData, t) {
    var billing = accountSummaryData.billing, conditions = accountSummaryData.conditions, credit = accountSummaryData.credit, installment = accountSummaryData.installment;
    var introRateLoan = billing.introRateLoan, nextPaymentDate = billing.nextPaymentDate, totalMinimumPaymentDue = billing.totalMinimumPaymentDue, lastPaymentDate = billing.lastPaymentDate, lastPaymentAmount = billing.lastPaymentAmount, fixedPaymentAmount = billing.fixedPaymentAmount;
    var isClosed = isLoanClosed(conditions);
    var isPreDelivery = isPredeliveryAndTotalPaymentDueZero(conditions, totalMinimumPaymentDue);
    var isClosedORPreDelivery = isClosed || isPreDelivery;
    var unavailableText = t("error.uiComponent");
    var getDataElementType = function (shouldShowUnavailableText, type) {
        return shouldShowUnavailableText ? "text" : type;
    };
    var getDataElementValue = function (shouldShowUnavailableText, value) {
        return shouldShowUnavailableText ? unavailableText : value;
    };
    var loanTerm = installment.loanTerm, interestRate = installment.interestRate, endDate = installment.endDate, startDate = installment.startDate;
    var _a = introRateLoan || {}, promoMonthlyPaymentAmount = _a.promoMonthlyPaymentAmount, promoLoanTerm = _a.promoLoanTerm, promoInterestRate = _a.promoInterestRate, promoExpiryDate = _a.promoExpiryDate, promoStatus = _a.promoStatus;
    var autopayValue = autopayStatusData.enrollment
        ? "grid.labels.autoPayStatus.on"
        : "grid.labels.autoPayStatus.off";
    var isActiveIRL = (promoStatus === null || promoStatus === void 0 ? void 0 : promoStatus.includes("ACTIVE")) || (promoStatus === null || promoStatus === void 0 ? void 0 : promoStatus.includes("EXPIRING"));
    var promotionalData = isActiveIRL
        ? {
            stepperAdditionalText: t("introRateLoan.activeIntroLoanDetails"),
            promoStatusTooltipText: t("grid.labels.activePromoStatusTooltip"),
            promotionalStatus: t("introRateLoan.introStatusActive"),
            promoStepLabel: t("grid.labels.introPeriodEnds"),
            introRateLoanInformationTooltip: t("grid.labels.activeIntroRateLoanInformationTooltip"),
        }
        : {
            stepperAdditionalText: t("introRateLoan.expiredIntroLoanDetails"),
            promoStatusTooltipText: t("grid.labels.expiredPromoStatusTooltip"),
            promotionalStatus: t("introRateLoan.introStatusExpired"),
            promoStepLabel: t("grid.labels.introPeriodEnded"),
            introRateLoanInformationTooltip: t("grid.labels.expiredIntroRateLoanInformationTooltip"),
        };
    var loanStartDateStep = (_jsx(Step, { label: t("grid.labels.loanStartDate"), value: startDate && formatDisplayDate(startDate) }));
    var promoEndDateStep = (_jsx(Step, { labelWeight: "bold", label: promotionalData.promoStepLabel, value: promoExpiryDate && formatDisplayDate(promoExpiryDate) }));
    var loanEndDateStep = (_jsx(Step, { label: t("grid.labels.loanEndDate"), value: endDate && formatDisplayDate(endDate) }));
    var subAdditionalInfoForPeriods = getSubAdditionalInfoForPeriods(isClosedORPreDelivery, t, isActiveIRL, promoExpiryDate);
    // This is needed to align the rate next to the  Monthly Payment in the row.
    var blankSubAdditionalLabel = {
        displayText: "",
    };
    return {
        dataSections: [
            {
                sectionName: "loan-summary",
                heading: t("grid.titles.loanSummary"),
                dataElements: [
                    {
                        label: t("grid.labels.loanStartDate"),
                        value: startDate,
                        type: "date",
                    },
                    {
                        label: t("grid.labels.loanTerm"),
                        value: loanTerm,
                        type: "months",
                    },
                    {
                        label: t("grid.labels.estimatedPayoffAmount"),
                        value: credit === null || credit === void 0 ? void 0 : credit.currentBalance,
                        type: "amount",
                        toolTipText: t("grid.labels.estimatedPayoffAmountTooltip"),
                    },
                ],
            },
            {
                heading: t("grid.titles.payments"),
                sectionName: "payments",
                dataElements: [
                    {
                        value: getDataElementValue(isClosedORPreDelivery, nextPaymentDate),
                        label: t("grid.labels.nextPaymentDate"),
                        type: getDataElementType(isClosedORPreDelivery, "date"),
                    },
                    {
                        value: getDataElementValue(isClosedORPreDelivery, totalMinimumPaymentDue),
                        label: t("grid.labels.nextAmountDue"),
                        type: getDataElementType(isClosedORPreDelivery, "amount"),
                        toolTipText: t("grid.labels.nextAmountMothlyTooltip"),
                    },
                    {
                        value: getDataElementValue(isPreDelivery, lastPaymentDate),
                        label: t("grid.labels.lastPaymentDate"),
                        type: getDataElementType(isPreDelivery, "date"),
                    },
                    {
                        value: getDataElementValue(isPreDelivery, lastPaymentAmount),
                        label: t("grid.labels.lastPaymentAmount"),
                        type: getDataElementType(isPreDelivery, "amount"),
                    },
                    {
                        value: t(autopayValue),
                        label: t("grid.labels.autoPayStatus.text"),
                        type: "text",
                    },
                ],
            },
            {
                heading: t("grid.titles.loanInformation"),
                sectionName: "loan-information",
                hasStepper: true,
                headerToolTipText: (_jsx(TooltipWrapper, { children: _jsx(StyledToolTip, __assign({ placement: "bottom", isInteractive: false, ariaLabel: t("grid.titles.loanInformation"), content: promotionalData.introRateLoanInformationTooltip }, { children: _jsx(FontAwesomeIcon, { icon: faQuestionCircle }) })) })),
                dataElements: __spreadArray(__spreadArray([
                    {
                        value: getDataElementValue(isPreDelivery, promoMonthlyPaymentAmount),
                        label: t("grid.labels.monthlyPayment"),
                        type: getDataElementType(isPreDelivery, "amount"),
                        additionalLabel: "(".concat(t("grid.labels.introRateLoanMonthlyPayment", {
                            promoLoanTerm: promoLoanTerm,
                        }), ")"),
                        subAdditionalLabel: subAdditionalInfoForPeriods.introPeriodInfo,
                    },
                    {
                        value: promoInterestRate,
                        label: t("grid.labels.introPeriodRate"),
                        type: "percentage",
                        subAdditionalLabel: subAdditionalInfoForPeriods.introPeriodInfo
                            ? blankSubAdditionalLabel
                            : undefined,
                    },
                    {
                        value: getDataElementValue(isPreDelivery, fixedPaymentAmount),
                        label: t("grid.labels.monthlyPayment"),
                        type: getDataElementType(isPreDelivery, "amount"),
                        additionalLabel: "(".concat(t("grid.labels.postIntroLoanMonthlyPayment", {
                            postLoanTerm: Number(loanTerm) - Number(promoLoanTerm),
                        }), ")"),
                        subAdditionalLabel: subAdditionalInfoForPeriods.postIntroPeriodInfo,
                    },
                    {
                        value: interestRate,
                        label: t("grid.labels.postIntroRate"),
                        type: "percentage",
                        subAdditionalLabel: subAdditionalInfoForPeriods.postIntroPeriodInfo
                            ? blankSubAdditionalLabel
                            : undefined,
                    }
                ], (promoExpiryDate && endDate && startDate
                    ? [
                        {
                            progressPercentage: calculateStepperProgress({
                                startDate: startDate,
                                endDate: endDate,
                            }),
                            startStep: {
                                stepData: loanStartDateStep,
                            },
                            endStep: {
                                stepData: loanEndDateStep,
                                className: "endStepBackGroundColor",
                            },
                            milestoneStep: {
                                stepData: promoEndDateStep,
                                value: (Number(promoLoanTerm) * 100) / Number(loanTerm),
                                stepIcon: (_jsx(ExclamationWrapper, { children: _jsx(FontAwesomeIcon, { icon: faExclamationTriangle }) })),
                            },
                            additionalText: promotionalData.stepperAdditionalText,
                            dataCy: "intro-rate-promotion",
                        },
                    ]
                    : []), true), [
                    {
                        value: promoExpiryDate,
                        label: t("grid.labels.introPeriodExpirationDate"),
                        type: "date",
                    },
                    {
                        value: endDate,
                        label: t("grid.labels.loanEndDate"),
                        type: "date",
                    },
                    {
                        value: promotionalData.promotionalStatus,
                        label: t("grid.labels.introPeriodStatus"),
                        type: "text",
                        toolTipText: promotionalData.promoStatusTooltipText,
                    },
                ], false),
            },
        ],
    };
};
export default getIntroRateLoanDataSections;
//# sourceMappingURL=getIntroRateLoanDataSections.js.map